import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { SelectField } from "@makula/ui";
import { ContractDetailPanel, ContractSLAAccordion } from "../components/ContractDetailPanel/ContractDetailPanel";

// ─── Types ────────────────────────────────────────────────────────────────────

type ContractType = "company" | "asset";
type ContractStatus = "active" | "expiring_soon" | "expired" | "draft";
type SLAPriority = "Critical" | "High" | "Medium" | "Low";
type TimeUnit = "hours" | "days";
type TabFilter = "All" | "Company" | "Asset";
type LaborRateUnit = "hourly" | "daily" | "weekly";
type TravelRateUnit = "per_km" | "per_day" | "per_trip";

interface SLALevel {
  id: string;
  priority: SLAPriority;
  responseTime: number;
  responseUnit: TimeUnit;
  resolutionTime: number;
  resolutionUnit: TimeUnit;
}

interface Contract {
  id: string;
  contractNumber: string;
  type: ContractType;
  companyId?: string;
  companyName?: string;
  assetId?: string;
  assetName?: string;
  effectiveDate: string;
  expiryDate: string;
  paymentTerms: string;
  currency: string;
  exchangeRateType: "live" | "fixed";
  exchangeRate: number;
  laborRate: number;
  laborRateUnit?: LaborRateUnit;
  partsDiscountRate: number;
  travelRate: number;
  travelRateUnit?: TravelRateUnit;
  slaLevels: SLALevel[];
  status: ContractStatus;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const DEFAULT_SLA_LEVELS: SLALevel[] = [
  { id: "sla-1", priority: "Critical", responseTime: 4,  responseUnit: "hours", resolutionTime: 8,  resolutionUnit: "hours" },
  { id: "sla-2", priority: "High",     responseTime: 8,  responseUnit: "hours", resolutionTime: 24, resolutionUnit: "hours" },
  { id: "sla-3", priority: "Medium",   responseTime: 24, responseUnit: "hours", resolutionTime: 3,  resolutionUnit: "days"  },
  { id: "sla-4", priority: "Low",      responseTime: 48, responseUnit: "hours", resolutionTime: 5,  resolutionUnit: "days"  },
];

const MOCK_CONTRACTS: Contract[] = [
  {
    id: "c1",
    contractNumber: "CTR-2024-0041",
    type: "company",
    companyId: "acme-international",
    companyName: "Acme International",
    effectiveDate: "2024-01-01",
    expiryDate: "2026-12-31",
    paymentTerms: "Net 30", currency: "EUR", exchangeRateType: "fixed", exchangeRate: 1,
    laborRate: 85,
    partsDiscountRate: 15,
    travelRate: 1.2,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "active",
  },
  {
    id: "c2",
    contractNumber: "CTR-2025-0012",
    type: "asset",
    companyId: "acme-international",
    companyName: "Acme International",
    assetId: "M-2152",
    assetName: "CNC Machine A",
    effectiveDate: "2025-03-01",
    expiryDate: "2026-05-28",
    paymentTerms: "Net 60", currency: "USD", exchangeRateType: "live", exchangeRate: 1.08,
    laborRate: 95,
    partsDiscountRate: 20,
    travelRate: 1.5,
    slaLevels: [
      { id: "s1", priority: "Critical", responseTime: 2, responseUnit: "hours", resolutionTime: 4,  resolutionUnit: "hours" },
      { id: "s2", priority: "High",     responseTime: 4, responseUnit: "hours", resolutionTime: 12, resolutionUnit: "hours" },
    ],
    status: "expiring_soon",
  },
  {
    id: "c3",
    contractNumber: "CTR-2024-0088",
    type: "company",
    companyId: "beta-solutions",
    companyName: "Beta Solutions",
    effectiveDate: "2024-06-01",
    expiryDate: "2025-05-31",
    paymentTerms: "Net 90", currency: "GBP", exchangeRateType: "fixed", exchangeRate: 0.85,
    laborRate: 75,
    partsDiscountRate: 10,
    travelRate: 1.0,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "expired",
  },
  {
    id: "c4",
    contractNumber: "CTR-2025-0031",
    type: "asset",
    companyId: "gamma-enterprises",
    companyName: "Gamma Enterprises",
    assetId: "M-1987",
    assetName: "Lathe Machine B",
    effectiveDate: "2025-01-15",
    expiryDate: "2026-12-31",
    paymentTerms: "Net 30",
    laborRate: 90,
    partsDiscountRate: 18,
    travelRate: 1.3,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "active",
  },
  {
    id: "c5",
    contractNumber: "CTR-2026-0003",
    type: "company",
    companyId: "delta-corp",
    companyName: "Delta Corp",
    effectiveDate: "2026-04-01",
    expiryDate: "2027-03-31",
    paymentTerms: "Net 30",
    laborRate: 80,
    partsDiscountRate: 12,
    travelRate: 1.1,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "active",
  },
  {
    id: "c6",
    contractNumber: "CTR-2025-0055",
    type: "asset",
    companyId: "delta-corp",
    companyName: "Delta Corp",
    assetId: "M-2152",
    assetName: "Press Machine 2",
    effectiveDate: "2025-07-01",
    expiryDate: "2026-06-30",
    paymentTerms: "Net 60", currency: "CHF", exchangeRateType: "fixed", exchangeRate: 0.96,
    laborRate: 100,
    partsDiscountRate: 22,
    travelRate: 1.6,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "active",
  },
  {
    id: "c7",
    contractNumber: "CTR-2025-0072",
    type: "asset",
    companyId: "acme-packaging",
    companyName: "Acme Packaging Company",
    assetId: "PM-2000",
    assetName: "Packmaster 2000",
    effectiveDate: "2025-04-01",
    expiryDate: "2026-03-31",
    paymentTerms: "Net 30",
    laborRate: 90,
    partsDiscountRate: 18,
    travelRate: 1.4,
    slaLevels: DEFAULT_SLA_LEVELS,
    status: "active",
  },
];

const STATUS_CONFIG: Record<ContractStatus, { bg: string; color: string; label: string }> = {
  active:        { bg: "#ccefeb", color: "#00b09b", label: "ACTIVE" },
  expiring_soon: { bg: "#fff0e6", color: "#FF7A00", label: "EXPIRING SOON" },
  expired:       { bg: "#fde5f0", color: "#ee0064", label: "EXPIRED" },
  draft:         { bg: "#f3f4f6", color: "#748094", label: "DRAFT" },
};

const MOCK_COMPANIES = [
  { id: "acme-international",  name: "Acme International" },
  { id: "acme-packaging",      name: "Acme Packaging Company" },
  { id: "beta-solutions",      name: "Beta Solutions" },
  { id: "gamma-enterprises",   name: "Gamma Enterprises" },
  { id: "delta-corp",          name: "Delta Corp" },
  { id: "epsilon-industries",  name: "Epsilon Industries" },
  { id: "zeta-manufacturing",  name: "Zeta Manufacturing" },
];

const MOCK_ASSETS = [
  { id: "M-2152", name: "CNC Machine A",   companyId: "acme-international" },
  { id: "PM-2000", name: "Packmaster 2000", companyId: "acme-packaging" },
  { id: "M-3041", name: "HVAC Unit 3",     companyId: "beta-solutions" },
  { id: "M-1987", name: "Lathe Machine B", companyId: "gamma-enterprises" },
  { id: "M-2153", name: "Press Machine 2", companyId: "delta-corp" },
  { id: "M-4010", name: "Generator Unit",  companyId: "epsilon-industries" },
  { id: "M-5001", name: "Forklift #5",     companyId: "zeta-manufacturing" },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="3.5" cy="8" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="12.5" cy="8" r="1.25" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
    <circle cx="7" cy="7" r="5.5" />
    <path d="M7 6.5v4" />
    <circle cx="7" cy="4.5" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

function SortIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <path d="M6 2L8.5 5H3.5L6 2Z" fill="currentColor" />
      <path d="M6 10L3.5 7H8.5L6 10Z" fill="currentColor" />
    </svg>
  );
}

function ContractNumIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" />
      <path d="M4 5h6M4 7.5h4M4 10h2" />
    </svg>
  );
}

function TypeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4h8M3 7h6M3 10h4" />
    </svg>
  );
}

function CompanyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="10" height="8" rx="1" />
      <path d="M5 4V3a2 2 0 014 0v1M5 8h4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" />
      <path d="M1.5 6h11M4.5 1v3M9.5 1v3" />
    </svg>
  );
}

function RateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 2v10" />
      <path d="M9.5 4.5a2.5 2.5 0 00-5 0c0 2 5 2 5 4a2.5 2.5 0 01-5 0" />
    </svg>
  );
}

function SLAIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 4.5V7l2 1.5" />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4h1.5M3 7H11M3 10h8" />
      <rect x="5" y="3" width="6" height="2" rx="0.5" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContractStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: cfg.bg, color: cfg.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function TypeBadge({ type }: { type: ContractType }) {
  const isAsset = type === "asset";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: isAsset ? "#e6e8fe" : "#f3f4f6",
      color: isAsset ? "#0517f8" : "#748094",
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {isAsset ? "Asset" : "Company"}
    </span>
  );
}

// ─── Contract Detail Panel ────────────────────────────────────────────────────

// ─── New Contract Drawer ──────────────────────────────────────────────────────

interface NewContractDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (c: Omit<Contract, "id" | "status">) => void;
}

function NewContractDrawer({ open, onClose, onSave }: NewContractDrawerProps) {
  const [contractType,  setContractType]  = React.useState<ContractType>("company");
  const [companyId,     setCompanyId]     = React.useState("");
  const [assetId,       setAssetId]       = React.useState("");
  const [effectiveDate, setEffectiveDate] = React.useState("");
  const [expiryDate,    setExpiryDate]    = React.useState("");
  const [paymentTerms,     setPaymentTerms]     = React.useState("Net 30");
  const [currency,         setCurrency]         = React.useState("EUR");
  const [exchangeRateType, setExchangeRateType] = React.useState<"live" | "fixed">("fixed");
  const [exchangeRate,     setExchangeRate]     = React.useState<number>(1);
  const [liveRateLoading,  setLiveRateLoading]  = React.useState(false);
  const [laborRate,        setLaborRate]        = React.useState<number>(85);
  const [laborRateUnit,    setLaborRateUnit]    = React.useState<LaborRateUnit>("hourly");
  const [laborDiscount,    setLaborDiscount]    = React.useState<number>(0);
  const [laborMarginCap,   setLaborMarginCap]   = React.useState<number>(0);
  const [partsDiscount,    setPartsDiscount]    = React.useState<number>(15);
  const [partsMarginCap,   setPartsMarginCap]   = React.useState<number>(0);
  const [serviceDiscount,  setServiceDiscount]  = React.useState<number>(10);
  const [serviceMarginCap, setServiceMarginCap] = React.useState<number>(0);
  const [travelRate,       setTravelRate]       = React.useState<number>(1.2);
  const [travelRateUnit,   setTravelRateUnit]   = React.useState<TravelRateUnit>("per_km");
  const [travelDiscount,   setTravelDiscount]   = React.useState<number>(0);
  const [travelMarginCap,  setTravelMarginCap]  = React.useState<number>(0);

  React.useEffect(() => {
    if (open) {
      setContractType("company"); setCompanyId(""); setAssetId("");
      setEffectiveDate(""); setExpiryDate(""); setPaymentTerms("Net 30");
      setCurrency("EUR"); setExchangeRateType("fixed"); setExchangeRate(1); setLiveRateLoading(false);
      setLaborRate(85); setLaborRateUnit("hourly"); setLaborDiscount(0); setLaborMarginCap(0);
      setPartsDiscount(15); setPartsMarginCap(0); setServiceDiscount(10); setServiceMarginCap(0);
      setTravelRate(1.2); setTravelRateUnit("per_km"); setTravelDiscount(0); setTravelMarginCap(0);
    }
  }, [open]);

  const availableAssets = companyId ? MOCK_ASSETS.filter(a => a.companyId === companyId) : MOCK_ASSETS;
  const selectedCompany = MOCK_COMPANIES.find(c => c.id === companyId);
  const selectedAsset   = MOCK_ASSETS.find(a => a.id === assetId);

  function handleSave() {
    const contractNumber = `CTR-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    onSave({
      contractNumber, type: contractType,
      companyId: companyId || undefined, companyName: selectedCompany?.name,
      assetId: contractType === "asset" ? assetId || undefined : undefined,
      assetName: contractType === "asset" ? selectedAsset?.name : undefined,
      effectiveDate: effectiveDate || new Date().toISOString().slice(0, 10),
      expiryDate: expiryDate || "",
      paymentTerms, currency, exchangeRateType, exchangeRate,
      laborRate, laborRateUnit, partsDiscountRate: partsDiscount, travelRate, travelRateUnit,
      slaLevels: DEFAULT_SLA_LEVELS,
    });
    onClose();
  }

  if (!open) return null;

  const CURRENCIES = [
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
    { code: "JPY", symbol: "¥", name: "Japanese Yen" },
    { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
    { code: "AUD", symbol: "A$", name: "Australian Dollar" },
    { code: "SEK", symbol: "kr", name: "Swedish Krona" },
    { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
    { code: "DKK", symbol: "kr", name: "Danish Krone" },
    { code: "PLN", symbol: "zł", name: "Polish Zloty" },
    { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  ];
  const MOCK_LIVE_RATES: Record<string, number> = {
    USD: 1.08, GBP: 0.85, CHF: 0.96, JPY: 161.5, CAD: 1.47,
    AUD: 1.65, SEK: 11.35, NOK: 11.72, DKK: 7.46, PLN: 4.27, CNY: 7.82,
  };

  function handleCurrencyChange(code: string) {
    setCurrency(code);
    if (code === "EUR") { setExchangeRate(1); return; }
    if (exchangeRateType === "live") fetchLiveRate(code);
    else setExchangeRate(MOCK_LIVE_RATES[code] ?? 1);
  }

  function handleRateTypeChange(type: "live" | "fixed") {
    setExchangeRateType(type);
    if (type === "live" && currency !== "EUR") fetchLiveRate(currency);
  }

  function fetchLiveRate(code: string) {
    setLiveRateLoading(true);
    setTimeout(() => {
      setExchangeRate(MOCK_LIVE_RATES[code] ?? 1);
      setLiveRateLoading(false);
    }, 900);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", height: 38, borderRadius: 8,
    border: "1px solid var(--color-border-primary)",
    padding: "0 12px", fontSize: 13, fontWeight: 500,
    fontFamily: "var(--font-family-base)", background: "white",
    outline: "none", color: "var(--color-content-primary)",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
    color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
    marginBottom: 6, display: "block",
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: 14, fontWeight: 700, letterSpacing: "-0.28px",
    color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
    margin: "0 0 16px",
  };

  const unitSelectStyle: React.CSSProperties = {
    height: 38, borderRadius: 8,
    border: "1px solid var(--color-border-primary)",
    padding: "0 8px", fontSize: 12, fontWeight: 600,
    fontFamily: "var(--font-family-base)", background: "#ffffff",
    outline: "none", color: "var(--color-content-secondary)",
    cursor: "pointer", flexShrink: 0,
    appearance: "none", WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23748094' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 8px center",
    paddingRight: 28,
  };

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 100 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 520, height: "100vh",
        background: "var(--color-bg-primary)", zIndex: 101,
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
            New Contract
          </span>
          <button onClick={onClose} style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, color: "var(--color-content-secondary)" }}>
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>

          {/* Type toggle */}
          <div style={{ marginBottom: 20 }}>
            <span style={{ ...labelStyle }}>Contract Type</span>
            <div style={{ display: "flex", border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
              {(["company", "asset"] as ContractType[]).map((t) => {
                const active = contractType === t;
                return (
                  <button
                    key={t}
                    onClick={() => { setContractType(t); setAssetId(""); }}
                    style={{
                      flex: 1, padding: "9px 0", border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 700, fontFamily: "var(--font-family-base)",
                      background: active ? "var(--color-bg-brand-dark)" : "white",
                      color: active ? "white" : "#748094",
                      borderRight: t === "company" ? "1px solid var(--color-border-primary)" : "none",
                      transition: "all 100ms ease",
                    }}
                  >
                    {t === "company" ? "Company Contract" : "Asset Contract"}
                  </button>
                );
              })}
            </div>
            {contractType === "asset" && (
              <div style={{
                marginTop: 8, padding: "8px 10px", borderRadius: 6,
                background: "#e6f9f1", border: "1px solid #a7f3d0",
                fontSize: 12, fontWeight: 500, color: "#065f46",
                fontFamily: "var(--font-family-base)", display: "flex", gap: 7, alignItems: "flex-start",
              }}>
                <InfoIcon />
                <span>Asset contracts override company contracts for quotes, invoices &amp; work orders.</span>
              </div>
            )}
          </div>

          {/* Company */}
          <SelectField
            label="Company"
            required
            value={companyId}
            placeholder="Select a company…"
            onChange={(v) => { setCompanyId(v); setAssetId(""); }}
            options={MOCK_COMPANIES.map(c => ({ label: c.name, value: c.id }))}
            style={{ marginBottom: 16 }}
          />

          {/* Asset */}
          {contractType === "asset" && (
            <SelectField
              label="Asset"
              required
              value={assetId}
              placeholder={!companyId ? "Select a company first" : "Select an asset…"}
              onChange={setAssetId}
              options={availableAssets.map(a => ({ label: `${a.name} · ${a.id}`, value: a.id }))}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Effective Date <span style={{ color: "#ee0064" }}>*</span></label>
              <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Expiry Date <span style={{ color: "#ee0064" }}>*</span></label>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Payment Terms + Currency */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <SelectField
              label="Payment Terms"
              value={paymentTerms}
              onChange={setPaymentTerms}
              options={["Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "Due on Receipt"].map(v => ({ label: v, value: v }))}
            />
            <div>
              <label style={labelStyle}>Currency</label>
              <select
                value={currency}
                onChange={e => handleCurrencyChange(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23748094' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", paddingRight: 32 }}
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Exchange Rate — only shown when currency != EUR */}
          {currency !== "EUR" && (
            <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden", marginBottom: 24 }}>
              <div style={{ padding: "8px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Exchange Rate</span>
                <div style={{ display: "flex", border: "1px solid var(--color-border-primary)", borderRadius: 6, overflow: "hidden" }}>
                  {(["live", "fixed"] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => handleRateTypeChange(t)}
                      style={{
                        padding: "3px 10px", border: "none", cursor: "pointer",
                        fontSize: 11, fontWeight: 700, fontFamily: "var(--font-family-base)",
                        background: exchangeRateType === t ? "var(--color-bg-brand-dark)" : "white",
                        color: exchangeRateType === t ? "white" : "var(--color-content-secondary)",
                        borderRight: t === "live" ? "1px solid var(--color-border-primary)" : "none",
                        transition: "all 100ms ease",
                      }}
                    >
                      {t === "live" ? "Live Rate" : "Fixed Rate"}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ padding: "12px" }}>
                {exchangeRateType === "live" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, height: 38, border: "1px solid var(--color-border-primary)", borderRadius: 8, padding: "0 12px", display: "flex", alignItems: "center", background: "var(--color-bg-accent)" }}>
                      <span style={{ fontSize: 13, fontFamily: "var(--font-family-base)", color: "var(--color-content-secondary)" }}>
                        {liveRateLoading ? "Fetching live rate…" : `1 EUR = ${exchangeRate.toFixed(4)} ${currency}`}
                      </span>
                    </div>
                    <button
                      onClick={() => fetchLiveRate(currency)}
                      disabled={liveRateLoading}
                      style={{ height: 38, padding: "0 14px", border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "white", cursor: liveRateLoading ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-family-base)", color: "var(--color-content-secondary)", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ animation: liveRateLoading ? "spin 1s linear infinite" : "none" }}>
                        <path d="M13 7A6 6 0 1 1 7 1" /><path d="M13 1v6h-6" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                ) : (
                  <div>
                    <label style={{ ...labelStyle, marginBottom: 6 }}>1 EUR =</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ position: "relative", flex: 1 }}>
                        <input
                          type="number" min={0} step={0.0001}
                          value={exchangeRate}
                          onChange={e => setExchangeRate(Number(e.target.value))}
                          style={inputStyle}
                        />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", flexShrink: 0 }}>{currency}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {currency === "EUR" && <div style={{ marginBottom: 24 }} />}

          <div style={{ height: 1, background: "var(--color-border-primary)", margin: "0 0 24px" }} />

          {/* Rates */}
          <p style={sectionLabel}>Rates &amp; Discounts</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>

            {/* Labor */}
            <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Labor</span>
              </div>
              <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Rate</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>€</span>
                      <input type="number" min={0} step={0.01} value={laborRate} onChange={e => setLaborRate(Number(e.target.value))} style={{ ...inputStyle, paddingLeft: 22 }} />
                    </div>
                    <select value={laborRateUnit} onChange={e => setLaborRateUnit(e.target.value as LaborRateUnit)} style={unitSelectStyle}>
                      <option value="hourly">/ hr</option>
                      <option value="daily">/ day</option>
                      <option value="weekly">/ week</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Discount (%)</label>
                    <div style={{ position: "relative" }}>
                      <input type="number" min={0} max={100} step={0.1} value={laborDiscount} onChange={e => setLaborDiscount(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Margin Cap (%)</label>
                    <div style={{ position: "relative" }}>
                      <input type="number" min={0} max={100} step={0.1} value={laborMarginCap} onChange={e => setLaborMarginCap(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parts */}
            <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Parts</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "12px" }}>
                <div>
                  <label style={labelStyle}>Discount (%)</label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} max={100} step={0.1} value={partsDiscount} onChange={e => setPartsDiscount(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Margin Cap (%)</label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} max={100} step={0.1} value={partsMarginCap} onChange={e => setPartsMarginCap(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service */}
            <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Service</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "12px" }}>
                <div>
                  <label style={labelStyle}>Discount (%)</label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} max={100} step={0.1} value={serviceDiscount} onChange={e => setServiceDiscount(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Margin Cap (%)</label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} max={100} step={0.1} value={serviceMarginCap} onChange={e => setServiceMarginCap(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Travel */}
            <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Travel</span>
              </div>
              <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Rate</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>€</span>
                      <input type="number" min={0} step={0.01} value={travelRate} onChange={e => setTravelRate(Number(e.target.value))} style={{ ...inputStyle, paddingLeft: 22 }} />
                    </div>
                    <select value={travelRateUnit} onChange={e => setTravelRateUnit(e.target.value as TravelRateUnit)} style={unitSelectStyle}>
                      <option value="per_km">/ km</option>
                      <option value="per_day">/ day</option>
                      <option value="per_trip">/ trip</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Discount (%)</label>
                    <div style={{ position: "relative" }}>
                      <input type="number" min={0} max={100} step={0.1} value={travelDiscount} onChange={e => setTravelDiscount(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Margin Cap (%)</label>
                    <div style={{ position: "relative" }}>
                      <input type="number" min={0} max={100} step={0.1} value={travelMarginCap} onChange={e => setTravelMarginCap(Number(e.target.value))} style={{ ...inputStyle, paddingRight: 28 }} />
                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div style={{ height: 1, background: "var(--color-border-primary)", margin: "0 0 24px" }} />

          {/* SLA Configuration */}
          <p style={sectionLabel}>SLA Configuration</p>
          <ContractSLAAccordion />
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--color-border-primary)", flexShrink: 0 }}>
          <button
            onClick={handleSave}
            style={{
              width: "100%", height: 44, border: "none", borderRadius: 8,
              background: "var(--color-bg-brand-dark)",
              color: "#fff", fontSize: 14, fontWeight: 700,
              fontFamily: "var(--font-family-base)", cursor: "pointer", letterSpacing: "-0.2px",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#0311c5"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-bg-brand-dark)"; }}
          >
            Create Contract
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS: TabFilter[] = ["All", "Company", "Asset"];

export function ContractsListPage() {
  const [activeTab,        setActiveTab]        = React.useState<TabFilter>("All");
  const [contracts,        setContracts]        = React.useState<Contract[]>(MOCK_CONTRACTS);
  const [hoveredRow,       setHoveredRow]       = React.useState<string | null>(null);
  const [drawerOpen,       setDrawerOpen]       = React.useState(false);
  const [selectedContract, setSelectedContract] = React.useState<Contract | null>(null);
  const [sortCol,          setSortCol]          = React.useState<string | null>(null);
  const [sortDir,          setSortDir]          = React.useState<"asc" | "desc">("asc");

  const tabCounts = {
    All:     contracts.length,
    Company: contracts.filter(c => c.type === "company").length,
    Asset:   contracts.filter(c => c.type === "asset").length,
  };

  const filteredRows = React.useMemo(() => {
    let list = [...contracts];
    if (activeTab === "Company") list = list.filter(c => c.type === "company");
    if (activeTab === "Asset")   list = list.filter(c => c.type === "asset");
    if (sortCol) {
      list.sort((a, b) => {
        const va = String((a as unknown as Record<string, unknown>)[sortCol] ?? "");
        const vb = String((b as unknown as Record<string, unknown>)[sortCol] ?? "");
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return list;
  }, [contracts, activeTab, sortCol, sortDir]);

  function handleSort(col: string) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  function handleCreateContract(data: Omit<Contract, "id" | "status">) {
    const status: ContractStatus = !data.expiryDate ? "draft" :
      new Date(data.expiryDate) < new Date() ? "expired" :
      new Date(data.expiryDate) < new Date(Date.now() + 30 * 86400_000) ? "expiring_soon" :
      "active";
    setContracts(prev => [{ ...data, id: `c-${Date.now()}`, status }, ...prev]);
  }

  const colHeaderStyle: React.CSSProperties = {
    background: "white",
    borderBottom: "1px solid var(--color-border-primary)",
    padding: "0 12px", height: 34,
    whiteSpace: "nowrap", textAlign: "left", fontWeight: "normal",
  };

  const colHeaderInner: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    fontSize: 12, fontWeight: 500, color: "#748094",
    cursor: "pointer", userSelect: "none", fontFamily: "var(--font-family-base)",
  };

  const cellStyle: React.CSSProperties = {
    padding: "0 12px", height: 48,
    fontSize: 12, fontWeight: 500,
    color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
    borderBottom: "1px solid var(--color-border-primary)",
    verticalAlign: "middle", overflow: "hidden",
    textOverflow: "ellipsis", whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <PageHeader
        breadcrumbs={[{ label: "Contracts" }]}
        searchAction={() => {}}
        primaryAction={{ label: "New Contract", onClick: () => setDrawerOpen(true) }}
      />

      <div style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-primary)" }}>

        {/* Content card */}
        <div style={{ margin: "24px 24px 24px", border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>

          {/* Tabs */}
          <div style={{ borderBottom: "1px solid var(--color-border-primary)", padding: "16px 0 0" }}>
            <div style={{ display: "flex", gap: 0 }}>
              {TABS.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: "0 8px 12px", marginRight: 4,
                      border: "none",
                      borderBottom: active ? "2px solid var(--color-bg-brand-dark)" : "2px solid transparent",
                      background: "transparent", cursor: "pointer",
                      fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                      color: active ? "var(--color-content-primary)" : "#a2aab8",
                      whiteSpace: "nowrap", letterSpacing: "-0.24px", lineHeight: "16px",
                      transition: "color 120ms ease, border-color 120ms ease",
                    }}
                  >
                    {tab} ({tabCounts[tab]})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 148 }} />
                <col style={{ width: 110 }} />
                <col style={{ minWidth: 160 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "contractNumber", label: "Contract",       icon: <ContractNumIcon />, sortable: true  },
                    { key: "type",           label: "Type",            icon: <TypeIcon />,        sortable: false },
                    { key: "companyName",    label: "Linked To",       icon: <CompanyIcon />,     sortable: true  },
                    { key: "effectiveDate",  label: "Effective Date",  icon: <CalendarIcon />,    sortable: true  },
                    { key: "expiryDate",     label: "Expiry Date",     icon: <CalendarIcon />,    sortable: true  },
                    { key: "status",         label: "Status",          icon: <StatusIcon />,      sortable: false },
                    { key: "__actions",      label: "",                icon: null,                sortable: false },
                  ].map(({ key, label, icon, sortable }) => (
                    <th key={key} style={colHeaderStyle}>
                      {label ? (
                        <span style={colHeaderInner} onClick={() => sortable && handleSort(key)}>
                          {icon}
                          {label}
                          {sortable && <SortIcon />}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => setSelectedContract(row)}
                    style={{
                      background: hoveredRow === row.id ? "var(--color-bg-accent)" : "var(--color-bg-primary)",
                      cursor: "pointer",
                      transition: "background 80ms ease",
                    }}
                  >
                    {/* Contract number */}
                    <td style={{ ...cellStyle, fontWeight: 700 }}>{row.contractNumber}</td>

                    {/* Type */}
                    <td style={cellStyle}>
                      <TypeBadge type={row.type} />
                    </td>

                    {/* Linked to */}
                    <td style={cellStyle}>
                      {row.type === "asset"
                        ? <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.assetName} · {row.assetId}</span>
                        : <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.companyName}</span>
                      }
                    </td>

                    {/* Effective Date */}
                    <td style={cellStyle}>{formatDate(row.effectiveDate)}</td>

                    {/* Expiry Date */}
                    <td style={cellStyle}>{formatDate(row.expiryDate)}</td>

                    {/* Status */}
                    <td style={cellStyle}>
                      <StatusBadge status={row.status} />
                    </td>

                    {/* Actions */}
                    <td
                      style={{ ...cellStyle, overflow: "visible", textOverflow: "unset", textAlign: "center" }}
                      onClick={e => e.stopPropagation()}
                    >
                      <button style={{ border: "none", background: "transparent", cursor: "pointer", color: "#748094", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 4, borderRadius: 4 }}>
                        <DotsIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRows.length === 0 && (
              <div style={{
                textAlign: "center", padding: "48px 24px",
                color: "var(--color-content-tertiary)", fontSize: 14,
                fontFamily: "var(--font-family-base)",
              }}>
                No contracts found
              </div>
            )}
          </div>
        </div>
      </div>

      <NewContractDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleCreateContract}
      />

      {selectedContract && (
        <ContractDetailPanel
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}
    </div>
  );
}
